import React, { useEffect, useState, useCallback } from 'react';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import * as cashflowService from '../../services/cashflowService';
import * as projectService from '../../services/projectService';
import CashFlowTabs from '../../components/CashFlowTabs';
import ScrollX from '../../components/ScrollX';
import { downloadBlob } from '../../utils/downloadFile';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Projects/CEO approve material requests - mirrors cashflow_routes.py's
// APPROVER_ROLES. Logging a requirement is open to anyone logged in, same
// convention as expense_routes.py (see that file's docstring).
const APPROVER_ROLES = ['HOD', 'CEO', 'System_Admin'];

const STATUS_COLORS = {
  pending_approval: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  ordered: 'bg-blue-100 text-blue-800',
  received: 'bg-gray-200 text-gray-800',
};

const initialForm = { project_id: '', material_name: '', quantity: '', unit: 'nos', unit_price: '', needed_by_date: '', notes: '' };

const MaterialsPage = () => {
  const { user } = useAuthStore();
  const showToast = useUIStore((s) => s.showToast);
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ project_id: '', status: '' });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const canApprove = user && APPROVER_ROLES.includes(user.role);
  const projectName = (id) => projects.find((p) => p.id === id)?.name || `#${id}`;

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.status) params.status = filters.status;
      const [projRes, matRes] = await Promise.all([
        projects.length ? Promise.resolve({ data: { projects } }) : projectService.getProjects(),
        cashflowService.getMaterials(params),
      ]);
      if (!projects.length) setProjects(projRes.data.projects || []);
      setRows(matRes.data.materials || []);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load material requirements', 'error');
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openAddForm = () => { setForm(initialForm); setEditingId(null); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(initialForm); };

  const handleEdit = (m) => {
    setForm({
      project_id: String(m.project_id), material_name: m.material_name, quantity: String(m.quantity),
      unit: m.unit, unit_price: String(m.unit_price), needed_by_date: m.needed_by_date, notes: m.notes || '',
    });
    setEditingId(m.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.material_name || !form.quantity || !form.unit_price || !form.needed_by_date || (!editingId && !form.project_id)) {
      showToast('Project, material, quantity, unit price, and needed-by date are required', 'error');
      return;
    }
    try {
      if (editingId) {
        // project_id isn't editable once raised - the API doesn't accept it
        // on update; changing project means raising a fresh requirement.
        await cashflowService.updateMaterial(editingId, {
          material_name: form.material_name, quantity: Number(form.quantity), unit: form.unit,
          unit_price: Number(form.unit_price), needed_by_date: form.needed_by_date, notes: form.notes || null,
        });
        showToast('Material requirement updated', 'success');
      } else {
        await cashflowService.createMaterial({
          project_id: Number(form.project_id), material_name: form.material_name, quantity: Number(form.quantity),
          unit: form.unit, unit_price: Number(form.unit_price), needed_by_date: form.needed_by_date,
          notes: form.notes || null,
        });
        showToast('Material requirement raised', 'success');
      }
      closeForm();
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to save requirement', 'error');
    }
  };

  const handleApprove = async (m) => {
    try {
      await cashflowService.approveMaterial(m.id);
      showToast('Approved - now counted in the forecast', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Approval failed', 'error');
    }
  };

  const handleReject = async (m) => {
    const reason = window.prompt('Reason for rejection:');
    if (!reason) return;
    try {
      await cashflowService.rejectMaterial(m.id, reason);
      showToast('Rejected', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Rejection failed', 'error');
    }
  };

  const handleMarkOrdered = async (m) => {
    try {
      await cashflowService.markMaterialOrdered(m.id);
      showToast('Marked ordered', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update', 'error');
    }
  };

  const handleMarkReceived = async (m) => {
    try {
      await cashflowService.markMaterialReceived(m.id);
      showToast('Marked received', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update', 'error');
    }
  };

  const handleMarkPaid = async (m) => {
    if (!window.confirm(`Mark "${m.material_name}" (${money(m.amount)}) as paid? It will stop showing up in the cash flow forecast.`)) return;
    try {
      await cashflowService.markMaterialPaid(m.id);
      showToast('Marked paid - removed from the forecast', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update', 'error');
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm('Delete this material requirement?')) return;
    try {
      await cashflowService.deleteMaterial(m.id);
      showToast('Deleted', 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete', 'error');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await cashflowService.getMaterialsImportTemplate();
      downloadBlob(res.data, 'material_requirements_template.xlsx');
    } catch (error) {
      showToast('Failed to download template', 'error');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIsImporting(true);
    try {
      const res = await cashflowService.importMaterials(file);
      const count = res.data.materials.length;
      showToast(`${count} material requirement${count !== 1 ? 's' : ''} imported`, 'success');
      loadAll();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to import file', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <CashFlowTabs />
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <h1 className="text-2xl font-bold text-white">Material Requirements</h1>
          <div className="flex gap-2">
            <button onClick={handleDownloadTemplate} className="text-xs px-3 py-2 rounded border border-white/40 text-white hover:bg-white/10">
              Download Template
            </button>
            <label className="text-xs px-3 py-2 rounded border border-white/40 text-white hover:bg-white/10 cursor-pointer">
              {isImporting ? 'Importing...' : 'Bulk Import'}
              <input type="file" accept=".xlsx,.xlsm" onChange={handleImport} className="hidden" disabled={isImporting} />
            </label>
            <button onClick={() => (showForm ? closeForm() : openAddForm())} className="text-sm px-4 py-2 rounded bg-white text-komal-blue font-semibold hover:bg-white/90">
              {showForm ? 'Cancel' : '+ Add Requirement'}
            </button>
          </div>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="glass-card p-5 mb-6">
            {editingId && (
              <p className="text-xs text-gray-500 mb-3">
                Editing requirement #{editingId}. Changing material, quantity, price, or date on an
                approved/ordered request sends it back for re-approval.
              </p>
            )}
            <div className="grid md:grid-cols-3 gap-3">
              <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                      disabled={!!editingId} className="border border-gray-300 rounded px-2 py-1.5 text-sm disabled:bg-gray-100 disabled:text-gray-400">
                <option value="">Select project *</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <input placeholder="Material name *" value={form.material_name} onChange={(e) => setForm({ ...form, material_name: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input type="date" value={form.needed_by_date} onChange={(e) => setForm({ ...form, needed_by_date: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input type="number" placeholder="Quantity *" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input placeholder="Unit (nos/kg/m...)" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input type="number" placeholder="Unit price (₹) *" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm md:col-span-2" />
              <button type="submit" className="text-sm px-4 py-1.5 rounded bg-komal-blue text-white font-semibold">{editingId ? 'Update' : 'Save'}</button>
            </div>
          </form>
        )}

        <div className="glass-card p-4 mb-6 flex flex-wrap gap-2 items-center">
          <select value={filters.project_id} onChange={(e) => setFilters({ ...filters, project_id: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All projects</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="">All statuses</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="ordered">Ordered</option>
            <option value="received">Received</option>
          </select>
        </div>

        <div className="glass-card">
          <div className="px-5 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Requirements ({rows.length})</h2>
            {!canApprove && <p className="text-xs text-gray-400 mt-0.5">Only Projects/CEO can approve or reject a requirement.</p>}
            <p className="text-xs text-gray-400 mt-0.5">
              Editable at any status - editing an approved/ordered request's material, quantity, price, or date
              sends it back for re-approval. An overdue, unpaid requirement still shows in the forecast (in the
              current period) rather than disappearing - mark it Paid once it's actually settled to drop it out.
            </p>
          </div>
          {isLoading ? (
            <p className="text-sm text-gray-400 p-5">Loading...</p>
          ) : rows.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No material requirements recorded yet.</p>
          ) : (
            <ScrollX>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-2 font-medium">Project</th>
                    <th className="px-4 py-2 font-medium">Material</th>
                    <th className="px-4 py-2 font-medium">Qty x price</th>
                    <th className="px-4 py-2 font-medium">Needed by</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Amount</th>
                    <th className="px-4 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <tr key={m.id} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-3">{projectName(m.project_id)}</td>
                      <td className="px-4 py-3">
                        {m.material_name}
                        {m.notes && <div className="text-xs text-gray-400 mt-0.5">{m.notes}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{m.quantity} {m.unit} x {money(m.unit_price)}</td>
                      <td className="px-4 py-3">{m.needed_by_date}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded font-semibold ${STATUS_COLORS[m.status]}`}>{m.status.replace('_', ' ')}</span>
                        {m.is_paid && (
                          <span className="ml-1 text-xs px-2 py-1 rounded font-semibold bg-emerald-100 text-emerald-800">paid</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium">{money(m.amount)}</td>
                      <td className="px-4 py-3 space-x-2 whitespace-nowrap">
                        {m.status === 'pending_approval' && canApprove && (
                          <>
                            <button onClick={() => handleApprove(m)} className="text-green-700 hover:underline text-xs font-semibold">Approve</button>
                            <button onClick={() => handleReject(m)} className="text-red-500 hover:underline text-xs font-semibold">Reject</button>
                          </>
                        )}
                        {m.status === 'approved' && (
                          <button onClick={() => handleMarkOrdered(m)} className="text-blue-700 hover:underline text-xs font-semibold">Mark Ordered</button>
                        )}
                        {(m.status === 'approved' || m.status === 'ordered') && (
                          <button onClick={() => handleMarkReceived(m)} className="text-gray-600 hover:underline text-xs font-semibold">Mark Received</button>
                        )}
                        {!m.is_paid && ['approved', 'ordered', 'received'].includes(m.status) && (
                          <button onClick={() => handleMarkPaid(m)} className="text-emerald-700 hover:underline text-xs font-semibold">Mark Paid</button>
                        )}
                        <button onClick={() => handleEdit(m)} className="text-blue-700 hover:underline text-xs font-semibold">Edit</button>
                        {m.status !== 'received' && !m.is_paid && (
                          <button onClick={() => handleDelete(m)} className="text-red-500 hover:underline text-xs font-semibold">Delete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaterialsPage;
